import React, { useEffect, useRef, useState } from 'react';
import {
    Heart, ArrowRight, Baby, Syringe, Globe, Share2, BadgeCheck, FileSignature, ListChecks, QrCode,
    PawPrint, Dog, Crown, Sparkles, Check, X as XIcon, Mail, ChevronDown,
} from 'lucide-react';
import {
    BREEDER_ELITE_MONTHLY, BREEDER_ELITE_YEARLY, BREEDER_PRO_MONTHLY, BREEDER_PRO_YEARLY, BREEDER_TRIAL_DAYS,
    COMPARE_ROWS, ELITE_FEATURES, FREE_FEATURES, PRO_FEATURES, clp,
} from '../../data/breederPlans';

/**
 * Portada pública de /criaderos (pestaña "Descubrir" de BreederPortalView).
 * (No confundir con BreederHome.tsx, que es el inicio DENTRO del panel del criador.)
 *
 * Regla de contenido: SOLO lo que existe hoy en el código (verificado el 2026-10-12):
 *  - Manada (breeder.php → manada) con estados reproductor / disponible / reservado / entregado.
 *  - Celos y montas con parto probable a 63 días y cuenta regresiva; "Registrar parto" crea la camada (BreederLitters).
 *  - Ficha: carnet de vacunas y desparasitaciones con alertas, curva de peso y talla, linaje, chip y QR.
 *  - Página pública amigopeludo.cl/criadero/:slug (BreederPublicPage); precio por ejemplar desde el plan Pro.
 *  - Traspaso: código enviado al correo del comprador; al aceptarlo, el ejemplar pasa a su cuenta (breeder.php, AP_SendEmail transfer_pet).
 *  - Sello de pedigrí: solicitud que revisa y aprueba un administrador (breeder_validation.php).
 *  - Contratos con firma electrónica simple y código al correo del comprador; lista de espera con aviso por correo (plan Élite).
 *  - Planes y límites desde src/data/breederPlans.ts (los mismos que aplica el servidor). El cobro en línea
 *    NO está conectado: Pro y Élite se activan escribiendo a hola@amigopeludo.cl.
 *
 * Las capturas de /public/portadas/criaderos son del panel y la página pública reales, con datos de ejemplo.
 */

const unsplash = (id: string, w = 900, h?: number) =>
    `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&q=80&w=${w}${h ? `&h=${h}` : ''}`;

const SHOT = {
    panelDesktop: '/portadas/criaderos/panel-escritorio.webp',
    panelMobile: '/portadas/criaderos/panel-movil.webp',
    camadas: '/portadas/criaderos/etapa-1-camadas.webp',
    crecimiento: '/portadas/criaderos/etapa-2-crecimiento.webp',
    publica: '/portadas/criaderos/etapa-3-pagina-publica.webp',
    traspaso: '/portadas/criaderos/etapa-4-traspaso.webp',
    manada: '/portadas/criaderos/manada.webp',
};

type Step = { icon: React.ElementType; title: string; text: string; shot: string; alt: string; caption: string; device: 'browser' | 'phone' };
const JOURNEY: Step[] = [
    {
        icon: Baby, title: 'Nace la camada', device: 'browser', shot: SHOT.camadas, alt: 'Celos, montas y camadas en el panel del criador', caption: 'Panel · Camadas y celos',
        text: 'Anotas el celo y la monta: el panel calcula el parto probable a 63 días y lleva la cuenta regresiva. Al parir, registras la camada y sus cachorros con sus padres.',
    },
    {
        icon: Syringe, title: 'Crece sano', device: 'browser', shot: SHOT.crecimiento, alt: 'Ficha de un cachorro con su curva de crecimiento y linaje', caption: 'Panel · Ficha del cachorro',
        text: 'Cada cachorro tiene su ficha: carnet de vacunas y desparasitaciones con alertas, curva de peso y talla, su linaje y su propio QR.',
    },
    {
        icon: Globe, title: 'Lo conocen', device: 'phone', shot: SHOT.publica, alt: 'Página pública del criadero en un celular', caption: 'Tu página pública · en celular',
        text: 'Tu criadero tiene su propia página, de la forma amigopeludo.cl/criadero/tu-criadero, con tus cachorros por camada, su estado y, desde el plan Pro, su precio.',
    },
    {
        icon: Share2, title: 'Se va con su familia', device: 'browser', shot: SHOT.traspaso, alt: 'Traspasos de cachorros a sus nuevos dueños', caption: 'Panel · Traspasos',
        text: 'Generas un código para el correo del comprador. Al aceptarlo, el cachorro pasa a su cuenta con todo su historial. Mientras está pendiente, puedes cancelarlo.',
    },
];

const TRUST: [React.ElementType, string, string, string?][] = [
    [BadgeCheck, 'Sello de pedigrí verificado', 'Declaras el registro y adjuntas el certificado. El equipo de Amigo Peludo lo revisa y, al aprobarlo, el ejemplar luce su sello.'],
    [QrCode, 'QR con alerta y ubicación', 'Si alguien escanea la placa de un ejemplar, su dueño recibe una alerta con la ubicación. Tras el traspaso, le llega al nuevo dueño.'],
    [FileSignature, 'Contratos con firma electrónica', 'El contrato se arma con los datos del cachorro. Tú lo firmas y el comprador firma desde su celular, validando un código en su correo.', 'Élite'],
    [ListChecks, 'Lista de espera', 'Los interesados se anotan desde tu página pública y te llega un correo cada vez que alguien se suma.', 'Élite'],
];

const FAQS = [
    { q: '¿Cómo firma el comprador un contrato?', a: 'Recibe un correo con el enlace al contrato, lo lee, pide un código de 6 dígitos que llega a ese mismo correo y firma con su nombre (y, si quiere, su firma dibujada con el dedo). Ambos quedan con una copia del documento.' },
    { q: '¿Qué muestra mi página pública?', a: 'Tu portada con logo, ubicación y sello de verificado, tus cachorros ordenados por camada, tus reproductores y, si lo activas desde el plan Pro, el precio de cada ejemplar. En el plan Élite suma un formulario de lista de espera. Tú decides qué ejemplares se ven.' },
    { q: '¿Cuánto cuesta?', a: `Puedes empezar gratis: hasta 8 ejemplares activos y 1 camada abierta. Criador Pro cuesta ${clp(BREEDER_PRO_MONTHLY)} al mes y Élite ${clp(BREEDER_ELITE_MONTHLY)} al mes. Al registrarte tienes ${BREEDER_TRIAL_DAYS} días de Pro de prueba; si no te convence, vuelves al plan Gratis sin perder nada.` },
    { q: '¿Cómo pago el plan Pro o Élite?', a: 'Por ahora nos escribes a hola@amigopeludo.cl y activamos tu plan. El pago en línea con Webpay, Mercado Pago y Flow está en preparación.' },
    { q: '¿Qué pasa si se pierde un cachorro?', a: 'Cada ejemplar tiene un QR. Si alguien escanea su placa, el dueño recibe una alerta con la ubicación. Al traspasar el cachorro, la alerta le llega al nuevo dueño.' },
];

const VALUES = ['Manada', 'Celos y montas', 'Camadas', 'Carnet sanitario', 'Pedigrí', 'Traspasos', 'Contratos', 'Crianza responsable'];

/* ─────────────────────────── estilos y animaciones ─────────────────────────── */
const HomeStyles: React.FC = () => (
    <>
        <link rel="stylesheet" precedence="default"
            href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,650;1,9..144,500;1,9..144,600&display=swap" />
        <style>{`
            .bh-serif { font-family: 'Fraunces', Georgia, serif; }
            .bh-grad-text { background: linear-gradient(100deg, #c97f17, #e0a83a 45%, #3f7d5c); -webkit-background-clip: text; background-clip: text; color: transparent; }
            @keyframes bhFloat { 0%,100% { transform: translateY(0) rotate(var(--r,0deg)); } 50% { transform: translateY(-12px) rotate(var(--r,0deg)); } }
            @keyframes bhBlob { 0%,100% { border-radius: 42% 58% 63% 37% / 41% 44% 56% 59%; transform: translate(0,0) scale(1); } 50% { border-radius: 58% 42% 38% 62% / 55% 61% 39% 45%; transform: translate(3%, -4%) scale(1.08); } }
            @keyframes bhPaw { 0% { transform: translateY(0) rotate(var(--r,0deg)); opacity: 0; } 15% { opacity: .5; } 100% { transform: translateY(-260px) rotate(calc(var(--r,0deg) + 25deg)); opacity: 0; } }
            @keyframes bhBeat { 0%, 100% { transform: scale(1); } 14% { transform: scale(1.18); } 28% { transform: scale(1); } 42% { transform: scale(1.12); } 70% { transform: scale(1); } }
            @keyframes bhMarquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
            @keyframes bhPop { 0% { opacity: 0; transform: translateY(14px) scale(.96); } 100% { opacity: 1; transform: none; } }
            @keyframes bhShine { 0% { transform: translateX(-120%) skewX(-18deg); } 60%,100% { transform: translateX(260%) skewX(-18deg); } }
            .bh-float { animation: bhFloat 6s ease-in-out infinite; }
            .bh-blob { animation: bhBlob 14s ease-in-out infinite; }
            .bh-beat { animation: bhBeat 1.6s ease-in-out infinite; }
            .bh-marquee { animation: bhMarquee 34s linear infinite; }
            .bh-pop { animation: bhPop .8s cubic-bezier(.2,.8,.2,1) both; }
            .bh-reveal { opacity: 0; transform: translateY(28px); transition: opacity .9s cubic-bezier(.2,.8,.2,1), transform .9s cubic-bezier(.2,.8,.2,1); }
            .bh-reveal.is-in { opacity: 1; transform: none; }
            .bh-line { transform-origin: top; transform: scaleY(0); transition: transform 1.8s cubic-bezier(.4,0,.2,1); }
            .is-in .bh-line { transform: scaleY(1); }
            @media (prefers-reduced-motion: reduce) {
                .bh-float, .bh-blob, .bh-beat, .bh-marquee, .bh-pop, .bh-paw { animation: none !important; }
                .bh-reveal, .bh-line { opacity: 1; transform: none; transition: none; }
            }
        `}</style>
    </>
);

const useInView = <T extends HTMLElement>(threshold = 0.2) => {
    const ref = useRef<T>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el || inView) return;
        if (typeof IntersectionObserver === 'undefined') { setInView(true); return; }
        const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } }, { threshold });
        io.observe(el);
        return () => io.disconnect();
    }, [inView, threshold]);
    return [ref, inView] as const;
};

const Reveal: React.FC<{ children: React.ReactNode; delay?: number; className?: string }> = ({ children, delay = 0, className = '' }) => {
    const [ref, inView] = useInView<HTMLDivElement>(0.15);
    return <div ref={ref} className={`bh-reveal ${inView ? 'is-in' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
};

const Eyebrow: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = 'text-amber-700' }) => (
    <p className={`text-[11px] font-bold uppercase tracking-[0.28em] mb-3 ${className}`}>{children}</p>
);

const BrowserFrame: React.FC<{ src: string; alt: string; className?: string }> = ({ src, alt, className = '' }) => (
    <div className={`rounded-2xl overflow-hidden bg-white ring-1 ring-amber-100 shadow-[0_30px_60px_-28px_rgba(80,55,15,.45)] ${className}`}>
        <div className="flex items-center gap-1.5 px-3.5 py-2.5 bg-[#fbf6ec] border-b border-amber-100">
            <span className="w-2.5 h-2.5 rounded-full bg-[#ff8a7a]" /><span className="w-2.5 h-2.5 rounded-full bg-[#ffd27a]" /><span className="w-2.5 h-2.5 rounded-full bg-[#9be0a6]" />
            <span className="ml-3 flex-1 truncate rounded-full bg-white px-3 py-0.5 text-[10px] text-[#9a8466] ring-1 ring-amber-100">amigopeludo.cl/criaderos</span>
        </div>
        <img src={src} alt={alt} loading="lazy" className="block w-full h-auto" />
    </div>
);

const PhoneFrame: React.FC<{ src: string; alt: string; className?: string }> = ({ src, alt, className = '' }) => (
    <div className={`rounded-[2.4rem] bg-[#2e2418] p-2 shadow-[0_30px_60px_-24px_rgba(60,40,10,.6)] ${className}`}>
        <div className="relative rounded-[2rem] overflow-hidden bg-white">
            <span className="absolute top-2 left-1/2 -translate-x-1/2 z-10 w-16 h-4 rounded-full bg-[#2e2418]" />
            <img src={src} alt={alt} loading="lazy" className="block w-full h-auto" />
        </div>
    </div>
);

const PawField: React.FC = () => (
    <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
        {[
            { l: '8%', b: '6%', s: 22, r: '-20deg', d: '9s', dl: '0s' }, { l: '24%', b: '0%', s: 16, r: '15deg', d: '11s', dl: '3s' },
            { l: '50%', b: '4%', s: 20, r: '-8deg', d: '10s', dl: '6s' }, { l: '70%', b: '2%', s: 14, r: '22deg', d: '12s', dl: '1.5s' },
            { l: '88%', b: '8%', s: 24, r: '-14deg', d: '9.5s', dl: '4.5s' },
        ].map((p, i) => (
            <PawPrint key={i} className="bh-paw absolute text-amber-300/70"
                style={{ left: p.l, bottom: p.b, width: p.s, height: p.s, ['--r' as any]: p.r, animation: `bhPaw ${p.d} ease-in ${p.dl} infinite` }} />
        ))}
    </div>
);

/* ─────────────────────────── secciones ─────────────────────────── */
const Hero: React.FC<{ onRegister: () => void; onLogin: () => void }> = ({ onRegister, onLogin }) => (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#fffaf0] via-[#fdf1dc] to-[#eef5ec]">
        <div aria-hidden className="bh-blob absolute -top-32 -left-24 w-[34rem] h-[34rem] bg-gradient-to-br from-amber-200/60 to-yellow-100/40 blur-3xl" />
        <div aria-hidden className="bh-blob absolute -bottom-40 right-[-10rem] w-[38rem] h-[38rem] bg-gradient-to-tr from-emerald-200/50 to-lime-100/40 blur-3xl" style={{ animationDelay: '-6s' }} />
        <PawField />

        <div className="relative max-w-7xl ml-auto mr-auto px-6 pt-28 md:pt-36 pb-20 md:pb-28 grid lg:grid-cols-[1fr_1.1fr] gap-14 items-center">
            <div className="text-center lg:text-left">
                <span className="bh-pop inline-flex items-center gap-2 bg-white/75 backdrop-blur border border-amber-100 text-amber-800 px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-[0.2em] shadow-sm">
                    <Heart size={13} className="bh-beat fill-rose-400 text-rose-400" /> Para criaderos responsables
                </span>
                <h1 className="bh-pop bh-serif mt-6 text-[2.6rem] sm:text-6xl lg:text-[4.2rem] leading-[1.03] font-semibold text-[#2e2418] tracking-[-0.02em]" style={{ animationDelay: '.1s' }}>
                    Cada cachorro, con <em className="bh-grad-text italic">su historia completa</em>
                </h1>
                <p className="bh-pop mt-6 text-lg text-[#6b5a44] leading-relaxed max-w-xl ml-auto mr-auto lg:ml-0 lg:mr-0" style={{ animationDelay: '.2s' }}>
                    Tu manada, sus celos y camadas, el carnet de cada cachorro y su traspaso a la nueva familia, en un panel pensado para criar con responsabilidad.
                </p>
                <div className="bh-pop mt-9 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start" style={{ animationDelay: '.3s' }}>
                    <button onClick={onRegister}
                        className="group relative overflow-hidden inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full bg-gradient-to-r from-[#d48a1c] to-[#3f7d5c] text-white font-bold text-sm tracking-wide shadow-[0_18px_40px_-14px_rgba(160,100,20,.6)] hover:-translate-y-0.5 transition-all">
                        <span aria-hidden className="absolute inset-y-0 left-0 w-1/3 bg-white/30" style={{ animation: 'bhShine 3.2s ease-in-out infinite' }} />
                        Solicitar mi panel de criador <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                    <button onClick={onLogin}
                        className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-white/80 backdrop-blur border border-amber-100 text-[#4a3a26] font-bold text-sm tracking-wide hover:bg-white transition-all">
                        Ya tengo cuenta
                    </button>
                </div>
                <p className="bh-pop mt-5 text-sm text-[#8a7458]" style={{ animationDelay: '.4s' }}>Empiezas gratis y con {BREEDER_TRIAL_DAYS} días de Criador Pro de prueba.</p>
            </div>

            <div className="relative ml-auto mr-auto w-full max-w-[38rem] pb-10 sm:pb-16">
                <div className="bh-pop relative" style={{ animationDelay: '.15s' }}>
                    <BrowserFrame src={SHOT.panelDesktop} alt="Resumen del panel del criadero en el computador" />
                </div>
                <div className="bh-float absolute -right-2 sm:-right-6 -bottom-2 w-[34%] max-w-[11.5rem]" style={{ ['--r' as any]: '3deg' }}>
                    <PhoneFrame src={SHOT.panelMobile} alt="Panel del criadero en el celular" />
                </div>
                <div className="bh-float absolute -left-3 sm:-left-8 -bottom-4 w-28 sm:w-36 aspect-square rounded-[1.75rem] overflow-hidden ring-[6px] ring-white shadow-xl" style={{ animationDelay: '-3s', ['--r' as any]: '-4deg' }}>
                    <img src={unsplash('1591160690555-5debfba289f0', 300, 300)} alt="" className="w-full h-full object-cover" />
                </div>
                <p className="absolute -bottom-6 left-0 right-0 text-center text-[10px] text-[#a8957a] italic">Capturas reales del panel, con datos de ejemplo</p>
            </div>
        </div>
        <svg aria-hidden viewBox="0 0 1440 90" preserveAspectRatio="none" className="relative block w-full h-12 md:h-20 text-white" fill="currentColor">
            <path d="M0 90V45C160 10 330 0 520 18c190 18 300 62 500 58 170-3 290-40 420-58v72H0Z" />
        </svg>
    </section>
);

const ValuesMarquee: React.FC = () => (
    <div className="bg-white py-6 overflow-hidden border-b border-amber-50">
        <div className="bh-marquee flex w-max gap-10 whitespace-nowrap">
            {[...VALUES, ...VALUES].map((v, i) => (
                <span key={i} className="bh-serif italic text-2xl md:text-3xl text-[#dcc79f] flex items-center gap-10">
                    {v} <PawPrint size={18} className="text-amber-300 not-italic" />
                </span>
            ))}
        </div>
    </div>
);

const Journey: React.FC = () => {
    const [ref, inView] = useInView<HTMLDivElement>(0.08);
    return (
        <section className="bg-white py-20 md:py-28">
            <div className="max-w-6xl ml-auto mr-auto px-6">
                <Reveal className="text-center max-w-2xl ml-auto mr-auto mb-16">
                    <Eyebrow>La vida de un cachorro en tu criadero</Eyebrow>
                    <h2 className="bh-serif text-4xl md:text-5xl font-semibold text-[#2e2418] tracking-[-0.02em] leading-tight">Del nacimiento a <em className="bh-grad-text italic">su nueva familia</em></h2>
                    <p className="mt-4 text-[#6b5a44] leading-relaxed">Cada etapa con la pantalla real que usas. Todo lo que registras queda en la ficha del cachorro.</p>
                </Reveal>

                <div ref={ref} className={`relative ${inView ? 'is-in' : ''}`}>
                    <div aria-hidden className="bh-line absolute left-6 md:left-1/2 top-2 bottom-2 w-[3px] md:-translate-x-1/2 rounded-full bg-gradient-to-b from-amber-300 via-amber-500 to-emerald-500" />
                    <div className="space-y-14 md:space-y-24">
                        {JOURNEY.map((s, i) => (
                            <Reveal key={s.title} delay={i * 100} className="relative grid md:grid-cols-2 gap-6 md:gap-16 items-center pl-16 md:pl-0">
                                <span className="absolute left-6 md:left-1/2 top-6 md:top-1/2 -translate-x-1/2 md:-translate-y-1/2 w-12 h-12 rounded-full bg-white border-4 border-amber-100 shadow-lg flex items-center justify-center z-10">
                                    <s.icon size={20} className="text-amber-700" />
                                </span>
                                <div className={`${i % 2 ? 'md:order-2 md:pl-12' : 'md:text-right md:pr-12'}`}>
                                    <p className="bh-serif text-amber-600 text-lg italic">Etapa {i + 1}</p>
                                    <h3 className="bh-serif text-2xl md:text-3xl font-semibold text-[#2e2418] mt-1">{s.title}</h3>
                                    <p className="text-[#6b5a44] leading-relaxed mt-3 md:max-w-sm md:inline-block">{s.text}</p>
                                </div>
                                <figure className={`${i % 2 ? 'md:order-1 md:pr-12' : 'md:pl-12'}`}>
                                    {s.device === 'phone'
                                        ? <PhoneFrame src={s.shot} alt={s.alt} className="w-[62%] max-w-[15rem] ml-auto mr-auto -rotate-2" />
                                        : <BrowserFrame src={s.shot} alt={s.alt} />}
                                    <figcaption className="mt-3 text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-[#a8957a]">{s.caption}</figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

/** La manada completa + las herramientas que dan confianza al comprador. */
const TrustSection: React.FC = () => (
    <section className="relative overflow-hidden py-20 md:py-28 bg-gradient-to-b from-[#fffaf1] to-[#f1f7ef]">
        <div aria-hidden className="bh-blob absolute top-10 -right-32 w-[26rem] h-[26rem] bg-amber-100/70 blur-3xl" />
        <div className="relative max-w-6xl ml-auto mr-auto px-6">
            <Reveal className="grid lg:grid-cols-[1fr_1.15fr] gap-12 items-center mb-14">
                <div>
                    <Eyebrow className="text-emerald-700">Confianza para quien compra</Eyebrow>
                    <h2 className="bh-serif text-4xl md:text-5xl font-semibold text-[#2e2418] tracking-[-0.02em] leading-tight">Toda tu manada, <em className="italic text-emerald-700">transparente</em></h2>
                    <p className="mt-4 text-[#6b5a44] leading-relaxed">Reproductores, cachorros disponibles, reservados y entregados, cada uno con su ficha. Y herramientas para que cada venta sea clara y segura.</p>
                </div>
                <BrowserFrame src={SHOT.manada} alt="Manada del criadero en el panel" />
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-4">
                {TRUST.map(([Icon, title, text, plan], i) => (
                    <Reveal key={title} delay={(i % 2) * 90}>
                        <div className="h-full rounded-[1.5rem] bg-white/80 backdrop-blur ring-1 ring-amber-100 p-6 flex gap-4 hover:-translate-y-1 hover:shadow-[0_20px_40px_-24px_rgba(80,55,15,.45)] transition-all">
                            <span className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center shrink-0"><Icon size={22} className="text-emerald-700" /></span>
                            <div>
                                <p className="font-bold text-[#2e2418] flex items-center gap-2 flex-wrap">{title}{plan && <span className="text-[10px] font-bold uppercase tracking-widest bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">Plan {plan}</span>}</p>
                                <p className="text-sm text-[#6b5a44] leading-relaxed mt-1.5">{text}</p>
                            </div>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const Cell: React.FC<{ v: boolean | string }> = ({ v }) =>
    typeof v === 'string' ? <span className="text-xs font-semibold text-[#4a3a26]">{v}</span>
        : v ? <Check size={16} className="text-emerald-600 ml-auto mr-auto" />
            : <XIcon size={15} className="text-[#cdbfa8] ml-auto mr-auto" />;

const Plans: React.FC<{ onRegister: () => void }> = ({ onRegister }) => {
    const [yearly, setYearly] = useState(false);
    const [showTable, setShowTable] = useState(false);
    const price = (m: number, y: number) => yearly
        ? <>{clp(y)}<span className="text-sm font-medium opacity-70">/año</span></>
        : <>{clp(m)}<span className="text-sm font-medium opacity-70">/mes</span></>;
    const card = 'h-full rounded-[2rem] p-7 flex flex-col';
    return (
        <section className="bg-white py-20 md:py-28">
            <div className="max-w-6xl ml-auto mr-auto px-6">
                <Reveal className="text-center max-w-2xl ml-auto mr-auto mb-10">
                    <Eyebrow>Planes</Eyebrow>
                    <h2 className="bh-serif text-4xl md:text-5xl font-semibold text-[#2e2418] tracking-[-0.02em] leading-tight">Empieza gratis, <em className="bh-grad-text italic">crece sin topes</em></h2>
                    <p className="mt-4 text-[#6b5a44] leading-relaxed">Al registrarte tienes {BREEDER_TRIAL_DAYS} días de Criador Pro de prueba. Después sigues gratis o eliges un plan; no se pierde nada de lo que registraste.</p>
                    <div className="mt-6 inline-flex rounded-full bg-[#f6efe2] p-1">
                        {([[false, 'Mensual'], [true, 'Anual']] as const).map(([v, l]) => (
                            <button key={l} onClick={() => setYearly(v)} aria-pressed={yearly === v}
                                className={`px-5 py-2 rounded-full text-xs font-bold transition-all ${yearly === v ? 'bg-white text-[#2e2418] shadow' : 'text-[#8a7458]'}`}>{l}</button>
                        ))}
                    </div>
                </Reveal>
                <div className="grid md:grid-cols-3 gap-5 items-stretch">
                    <Reveal>
                        <div className={`${card} bg-[#fbf7ef] ring-1 ring-amber-100`}>
                            <Dog size={22} className="text-amber-700" />
                            <h3 className="bh-serif text-2xl font-semibold mt-3">Gratis</h3>
                            <p className="text-sm text-[#8a7458]">Para partir y probar</p>
                            <p className="bh-serif text-4xl font-semibold mt-4">$0</p>
                            <ul className="mt-5 space-y-2.5 text-sm text-[#4a3a26] flex-1">{FREE_FEATURES.map(f => <li key={f} className="flex gap-2"><Check size={16} className="text-emerald-600 shrink-0 mt-0.5" />{f}</li>)}</ul>
                            <button onClick={onRegister} className="mt-7 py-3.5 rounded-full bg-[#2e2418] text-white font-bold text-sm hover:bg-black transition-all">Empezar gratis</button>
                        </div>
                    </Reveal>
                    <Reveal delay={100}>
                        <div className={`${card} relative bg-gradient-to-br from-[#2e2418] to-[#3d5a3f] text-white shadow-[0_40px_80px_-40px_rgba(40,30,10,.8)] md:-translate-y-3`}>
                            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-[#2e2418] text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">Recomendado</span>
                            <Crown size={22} className="text-amber-300" />
                            <h3 className="bh-serif text-2xl font-semibold mt-3">Criador Pro</h3>
                            <p className="text-sm text-white/70">Para criaderos que trabajan en serio</p>
                            <p className="bh-serif text-4xl font-semibold mt-4">{price(BREEDER_PRO_MONTHLY, BREEDER_PRO_YEARLY)}</p>
                            <ul className="mt-5 space-y-2.5 text-sm text-white/85 flex-1">{PRO_FEATURES.map(f => <li key={f} className="flex gap-2"><Check size={16} className="text-emerald-300 shrink-0 mt-0.5" />{f}</li>)}</ul>
                            <button onClick={onRegister} className="mt-7 py-3.5 rounded-full bg-amber-400 text-[#2e2418] font-bold text-sm hover:bg-amber-300 transition-all">Probar {BREEDER_TRIAL_DAYS} días gratis</button>
                        </div>
                    </Reveal>
                    <Reveal delay={200}>
                        <div className={`${card} bg-[#f1f7ef] ring-1 ring-emerald-100`}>
                            <Sparkles size={22} className="text-emerald-700" />
                            <h3 className="bh-serif text-2xl font-semibold mt-3">Criador Élite</h3>
                            <p className="text-sm text-[#8a7458]">Para vender con contrato y lista de espera</p>
                            <p className="bh-serif text-4xl font-semibold mt-4">{price(BREEDER_ELITE_MONTHLY, BREEDER_ELITE_YEARLY)}</p>
                            <ul className="mt-5 space-y-2.5 text-sm text-[#4a3a26] flex-1">{ELITE_FEATURES.map(f => <li key={f} className="flex gap-2"><Check size={16} className="text-emerald-600 shrink-0 mt-0.5" />{f}</li>)}</ul>
                            <button onClick={onRegister} className="mt-7 py-3.5 rounded-full bg-[#3f7d5c] text-white font-bold text-sm hover:bg-[#356a4e] transition-all">Quiero Élite</button>
                        </div>
                    </Reveal>
                </div>
                <Reveal className="mt-8 rounded-2xl bg-[#fbf7ef] ring-1 ring-amber-100 px-5 py-4 flex items-start gap-3 text-sm text-[#6b5a44] max-w-3xl ml-auto mr-auto">
                    <Mail size={18} className="text-amber-700 shrink-0 mt-0.5" />
                    <p>Para activar Pro o Élite nos escribes a <a href="mailto:hola@amigopeludo.cl" className="font-semibold text-amber-800 underline decoration-dotted">hola@amigopeludo.cl</a> y lo habilitamos. El pago en línea con Webpay, Mercado Pago y Flow está en preparación. El sello de verificado no depende del plan: se gana con el pedigrí aprobado.</p>
                </Reveal>
                <div className="mt-8 text-center">
                    <button onClick={() => setShowTable(s => !s)} className="inline-flex items-center gap-2 text-sm font-semibold text-amber-800">
                        {showTable ? 'Ocultar' : 'Comparar'} los planes en detalle <ChevronDown size={16} className={`transition-transform ${showTable ? 'rotate-180' : ''}`} />
                    </button>
                </div>
                {showTable && (
                    <div className="mt-6 overflow-x-auto rounded-2xl ring-1 ring-amber-100">
                        <table className="w-full min-w-[560px] text-sm">
                            <thead><tr className="bg-[#2e2418] text-white text-left"><th className="px-4 py-3 font-semibold">Función</th><th className="px-4 py-3 text-center">Gratis</th><th className="px-4 py-3 text-center">Pro</th><th className="px-4 py-3 text-center">Élite</th></tr></thead>
                            <tbody>
                                {COMPARE_ROWS.map(r => (
                                    <tr key={r.label} className="border-t border-amber-50 odd:bg-[#fffbf4]">
                                        <td className="px-4 py-2.5 text-[#4a3a26]">{r.label}</td>
                                        <td className="px-4 py-2.5 text-center"><Cell v={r.free} /></td>
                                        <td className="px-4 py-2.5 text-center"><Cell v={r.pro} /></td>
                                        <td className="px-4 py-2.5 text-center"><Cell v={r.elite} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </section>
    );
};

const Faq: React.FC = () => (
    <section className="bg-[#fffaf1] py-20 md:py-24">
        <div className="max-w-3xl ml-auto mr-auto px-6">
            <Reveal className="text-center mb-10">
                <Eyebrow>Preguntas frecuentes</Eyebrow>
                <h2 className="bh-serif text-4xl font-semibold text-[#2e2418] tracking-[-0.02em]">Lo que suelen preguntar</h2>
            </Reveal>
            <div className="space-y-3">
                {FAQS.map(f => (
                    <details key={f.q} className="group bg-white ring-1 ring-amber-100 rounded-2xl px-6 py-4 open:shadow-[0_20px_40px_-28px_rgba(80,55,15,.5)]">
                        <summary className="flex items-center justify-between gap-4 cursor-pointer list-none font-semibold text-[#2e2418] [&::-webkit-details-marker]:hidden">
                            {f.q}<ChevronDown size={18} className="shrink-0 text-amber-700 transition-transform group-open:rotate-180" />
                        </summary>
                        <p className="text-sm text-[#6b5a44] leading-relaxed mt-3">{f.a}</p>
                    </details>
                ))}
            </div>
        </div>
    </section>
);

const FinalCta: React.FC<{ onRegister: () => void; onLogin: () => void }> = ({ onRegister, onLogin }) => (
    <section className="relative overflow-hidden py-20 md:py-28 bg-gradient-to-br from-[#2e2418] via-[#3a3220] to-[#2f4a35] text-white">
        <div aria-hidden className="bh-blob absolute -top-24 right-[-6rem] w-[30rem] h-[30rem] bg-amber-400/20 blur-3xl" />
        <PawField />
        <div className="relative max-w-3xl ml-auto mr-auto px-6 text-center">
            <Reveal>
                <div className="ml-auto mr-auto w-16 h-16 rounded-full bg-white/10 ring-1 ring-white/20 flex items-center justify-center mb-6">
                    <Heart size={28} className="text-rose-300 fill-rose-300 bh-beat" />
                </div>
                <h2 className="bh-serif text-4xl md:text-5xl font-semibold tracking-[-0.02em] leading-tight">Ordena tu criadero <em className="italic text-amber-300">desde hoy</em></h2>
                <p className="mt-4 text-white/75 leading-relaxed max-w-xl ml-auto mr-auto">Envía tu solicitud: revisamos tus datos y habilitamos tu panel de criador. Si ya usas Amigo Peludo, es la misma cuenta.</p>
                <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
                    <button onClick={onRegister} className="inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full bg-gradient-to-r from-amber-400 to-amber-300 text-[#2e2418] font-bold text-sm shadow-[0_18px_40px_-14px_rgba(250,190,60,.6)] hover:-translate-y-0.5 transition-all">
                        Solicitar mi panel <ArrowRight size={17} />
                    </button>
                    <button onClick={onLogin} className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-white/10 text-white font-bold text-sm border border-white/25 hover:bg-white/20 transition-all">
                        Ingresar al panel
                    </button>
                </div>
            </Reveal>
        </div>
    </section>
);

export const BreederPortalHome: React.FC<{ onRegister: () => void; onLogin: () => void }> = ({ onRegister, onLogin }) => (
    <div className="font-sans text-[#2e2418] overflow-x-hidden">
        <HomeStyles />
        <Hero onRegister={onRegister} onLogin={onLogin} />
        <ValuesMarquee />
        <Journey />
        <TrustSection />
        <Plans onRegister={onRegister} />
        <Faq />
        <FinalCta onRegister={onRegister} onLogin={onLogin} />
    </div>
);

export default BreederPortalHome;
